import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  MoneyInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  getProductPricing,
  getVariantPricing,
  listBranches,
  listCompPlanConfigs,
  listHouseVariants,
  listMemberships,
  saveProductPricing,
  saveVariantPricing,
  type CompPlanConfigRow,
  type ProductPricing,
} from '../../lib/api';

type StairstepRank = { code: string; [k: string]: unknown };

/** Common shape for both product-level and variant-level segment dimensions. */
type DimensionRows = {
  memberships: Array<{ id: string; label: string }>;
  ranks: Array<{ id: string; label: string }>;
  branches: Array<{ id: string; label: string; sub?: string }>;
};

/**
 * Per-product ABSOLUTE pricing editor. Three typed tables (membership tier,
 * rank, stockist branch) — the admin types the final cash price for that
 * segment; blank = no override (falls back to the product's base price).
 * Replaces the old global Discount tab. NOT a raw-JSON editor.
 *
 * When the product has HOUSE variants, a target selector lets the admin edit
 * the whole product (the cascade default) OR an individual variant's prices,
 * which override the cascade (lowest applicable wins).
 */
export function PerProductPricingEditor({
  productId,
  basePrice,
}: {
  productId: string;
  basePrice: string;
}) {
  const variantsQ = useQuery({
    queryKey: ['house-variants', productId],
    queryFn: () => listHouseVariants(productId),
  });
  const dims = useSegmentDimensions();

  const variants = variantsQ.data ?? [];
  // Target: 'product' (whole product) or a variant id.
  const [target, setTarget] = useState('product');

  return (
    <div className="space-y-4">
      {variants.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Pricing for</span>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="h-8 w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="product">Whole product</SelectItem>
              {variants.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  Variation · {v.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {target === 'product' ? (
        <SegmentPricingTables
          key="product"
          dims={dims}
          basePrice={basePrice}
          queryKey={['product-pricing', productId]}
          load={() => getProductPricing(productId)}
          save={(body) => saveProductPricing(productId, body)}
          note={
            <>
              Set the final price each segment pays for this product. Blank = the base price (₱
              {fmt(basePrice)}). The lowest applicable price wins; PV is never discounted.
              {variants.length > 0 && (
                // The single most-reported pricing trap: an admin enters a
                // non-activated / member price here, the catalog tile shows it,
                // and then picking a variation jumps back to the variation's own
                // price. Variations deliberately don't inherit (schema/products.ts).
                <>
                  {' '}
                  <strong className="text-warning">
                    This product has variations — they do NOT inherit these prices.
                  </strong>{' '}
                  Buyers are charged the variation&apos;s price, so set each variation&apos;s prices
                  too using the selector above.
                </>
              )}
            </>
          }
        />
      ) : (
        <SegmentPricingTables
          key={target}
          dims={dims}
          basePrice={variants.find((v) => v.id === target)?.price ?? basePrice}
          queryKey={['variant-pricing', target]}
          load={() => getVariantPricing(productId, target)}
          save={(body) => saveVariantPricing(productId, target, body)}
          note={
            <>
              Per-variation absolute prices — the lowest applicable price wins. Blank = the
              variation&apos;s own base price; product-level discounts do NOT apply to variations.
              PV is never discounted.
            </>
          }
        />
      )}
    </div>
  );
}

/**
 * The three typed price tables + a single Save button, parameterized by where
 * the data comes from (product vs a variant). Re-seeds its drafts whenever the
 * loaded data changes; remounted (via `key`) when the target switches.
 */
function SegmentPricingTables({
  dims,
  basePrice,
  queryKey,
  load,
  save: saveFn,
  note,
}: {
  dims: DimensionRows;
  basePrice: string;
  queryKey: (string | number)[];
  load: () => Promise<ProductPricing>;
  save: (body: ProductPricing) => Promise<void>;
  note: ReactNode;
}) {
  const qc = useQueryClient();
  const pricingQ = useQuery({ queryKey, queryFn: load });

  const [membership, setMembership] = useState<Record<string, string>>({});
  const [rank, setRank] = useState<Record<string, string>>({});
  const [stockist, setStockist] = useState<Record<string, string>>({});
  const [nonActivated, setNonActivated] = useState('');

  useEffect(() => {
    if (!pricingQ.data) return;
    setMembership(
      Object.fromEntries(pricingQ.data.membership.map((r) => [r.membershipId, fmt(r.price)])),
    );
    setRank(Object.fromEntries(pricingQ.data.rank.map((r) => [r.rankCode, fmt(r.price)])));
    setStockist(Object.fromEntries(pricingQ.data.stockist.map((r) => [r.branchId, fmt(r.price)])));
    setNonActivated(pricingQ.data.nonActivated != null ? fmt(pricingQ.data.nonActivated) : '');
  }, [pricingQ.data]);

  const save = useMutation({
    mutationFn: () =>
      saveFn({
        membership: entries(membership).map(([membershipId, price]) => ({ membershipId, price })),
        rank: entries(rank).map(([rankCode, price]) => ({ rankCode, price })),
        stockist: entries(stockist).map(([branchId, price]) => ({ branchId, price })),
        nonActivated: nonActivated.trim() || null,
      }),
    meta: { successMessage: 'Pricing saved.' },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  if (pricingQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading pricing…</p>;
  }

  const placeholder = `Base ₱${fmt(basePrice)}`;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {note} 0 = deliberately free (in cash) for that segment — PV, commissions and kit codes
        still apply.
      </p>

      <PriceTable
        title="Membership tier prices"
        head="Tier"
        rows={dims.memberships}
        draft={membership}
        onChange={setMembership}
        placeholder={placeholder}
        emptyHint="No membership tiers."
      />

      <PriceTable
        title="Rank prices"
        head="Rank"
        rows={dims.ranks}
        draft={rank}
        onChange={setRank}
        placeholder={placeholder}
        emptyHint="No stairstep ranks defined."
      />

      <PriceTable
        title="Stockist / branch prices"
        head="Stockist / Branch"
        rows={dims.branches}
        draft={stockist}
        onChange={setStockist}
        placeholder={placeholder}
        emptyHint="No branches."
      />

      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-1">Non-activated members</h4>
        <div className="flex items-center gap-2">
          <MoneyInput
            className="h-8 w-32"
            value={nonActivated}
            placeholder={placeholder}
            onChange={(e) => setNonActivated(e.target.value)}
          />
          <span className="text-xs text-muted-foreground">
            Price for buyers with no activated slot. Blank = base price (or the tenant-level
            non-activated tier price, where configured).
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save prices'}
        </Button>
        {save.isSuccess && <span className="text-xs text-success">Saved</span>}
        {save.isError && <span className="text-xs text-destructive">Save failed</span>}
      </div>
    </div>
  );
}

function PriceTable({
  title,
  head,
  rows,
  draft,
  onChange,
  placeholder,
  emptyHint,
}: {
  title: string;
  head: string;
  rows: Array<{ id: string; label: string; sub?: string }>;
  draft: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  placeholder: string;
  emptyHint: string;
}) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-muted-foreground mb-1">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyHint}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground border-b">
            <tr>
              <th className="py-1 pr-2">{head}</th>
              <th className="py-1 pr-2 w-40">Price</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="py-1 pr-2">
                  {r.label}
                  {r.sub && (
                    <span className="ml-1 text-xs capitalize text-muted-foreground">· {r.sub}</span>
                  )}
                </td>
                <td className="py-1 pr-2">
                  <MoneyInput
                    className="h-8 w-32"
                    value={draft[r.id] ?? ''}
                    placeholder={placeholder}
                    onChange={(e) => onChange({ ...draft, [r.id]: e.target.value })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Non-blank draft entries, trimmed. */
function entries(draft: Record<string, string>): Array<[string, string]> {
  return Object.entries(draft)
    .map(([k, v]) => [k, v.trim()] as [string, string])
    .filter(([, v]) => v !== '');
}

function fmt(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : v;
}

/** Build the membership / rank / stockist dimension rows for the price tables. */
function useSegmentDimensions(): DimensionRows {
  const membershipsQ = useQuery({ queryKey: ['memberships'], queryFn: listMemberships });
  const branchesQ = useQuery({ queryKey: ['admin-branches'], queryFn: listBranches });
  const configsQ = useQuery({ queryKey: ['admin-compplan-configs'], queryFn: listCompPlanConfigs });

  const ranks = useMemo<StairstepRank[]>(() => {
    const rows = (configsQ.data ?? []).filter((r: CompPlanConfigRow) => r.planCode === 'stairstep');
    if (rows.length === 0) return [];
    const latest = rows.reduce((a, b) => (b.version > a.version ? b : a));
    return (latest.config?.ranks as StairstepRank[] | undefined) ?? [];
  }, [configsQ.data]);

  return {
    memberships: (membershipsQ.data ?? []).map((m) => ({
      id: m.id,
      label: `${m.name} (${m.code})`,
    })),
    ranks: ranks.map((r) => ({ id: r.code, label: r.code })),
    branches: (branchesQ.data ?? []).map((b) => ({
      id: b.id,
      label: `${b.name} (${b.code})`,
      sub: b.type,
    })),
  };
}

/**
 * Per-VARIATION absolute pricing editor — the same three segment price tables as
 * the whole-product editor, scoped to one house variant. Used by the Variations
 * tab's per-row "Discounts…" dialog; writes the same per-variant rows the
 * Discount tab's variation target does.
 */
export function VariantDiscountEditor({
  productId,
  variantId,
  basePrice,
}: {
  productId: string;
  variantId: string;
  basePrice: string;
}) {
  const dims = useSegmentDimensions();
  return (
    <SegmentPricingTables
      key={variantId}
      dims={dims}
      basePrice={basePrice}
      queryKey={['variant-pricing', variantId]}
      load={() => getVariantPricing(productId, variantId)}
      save={(body) => saveVariantPricing(productId, variantId, body)}
      note={
        <>
          Set the price each segment pays for THIS variation. Blank = the variation&apos;s own base
          price. The lowest applicable price wins; PV is never discounted.
        </>
      }
    />
  );
}
